// src/components/OneDrivePicker.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FaChevronRight } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import { getFileIcon } from "../../FileUploadTab";
import type { OneDriveId } from "@services/OneDriveService";
import {
  useOneDrivePickerFiles,
  useOneDriveFolderContents,
} from "./useOneDrivePicker";
import Loader from "@shared/components/Loader";

export interface FileItem {
  id: string;
  name: string;
  type: "file" | "folder";
  size?: string;
  modified: string;
  modifiedBy: string;
  filetype?: string; // e.g., "docx", "jpg"
  mimeType?: string; // from API: mime_type (files) or our synthetic for folders
  parentId?: string;
  children?: FileItem[];
}

export interface BreadcrumbItem {
  id: string;
  name: string;
}

export interface OneDriveFileUpload {
  id: string;
  name: string;
  mimeType: string;
}

export interface OneDrivePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: (selectedItems: OneDriveFileUpload[]) => void;
  initialFolderId?: OneDriveId | null;
}

/** ========= Helpers ========= */

const bytesToReadable = (bytes?: number | null): string | undefined => {
  if (bytes == null) return undefined;
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const val = bytes / Math.pow(k, i);
  return `${val.toFixed(val < 10 ? 2 : 1)} ${sizes[i]}`;
};

const getExtFromName = (name: string): string | undefined => {
  const idx = name.lastIndexOf(".");
  if (idx === -1 || idx === name.length - 1) return undefined;
  return name.slice(idx + 1).toLowerCase();
};

const normalizeBreadcrumbs = (
  apiCrumbs: Array<{ id: string; name: string }>
): BreadcrumbItem[] => {
  if (!apiCrumbs?.length) return [{ id: "root", name: "My files" }];
  const mapped = apiCrumbs.map((b, idx) => ({
    id: b.id,
    name: b.name === "root" && idx === 0 ? "My files" : b.name,
  }));
  if (mapped[0]?.name !== "My files") {
    return [{ id: "root", name: "My files" }, ...mapped];
  }
  return mapped;
};

/** Synthetic folder mime-type to store inside selection for folders */
const FOLDER_MIME = "application/vnd.onedrive-folder";

/** ========= Component ========= */

export const OneDrivePicker: React.FC<OneDrivePickerProps> = ({
  isOpen,
  onClose,
  onConfirm,
  initialFolderId = null,
}) => {
  /** Selection & navigation state */
  const [selectedItems, setSelectedItems] = useState<OneDriveFileUpload[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<OneDriveId | null>(
    initialFolderId ?? null
  );
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    { id: "root", name: "My files" },
  ]);

  // Track the latest folder id we've requested to avoid race conditions
  const latestFolderIdRef = useRef<OneDriveId | null>(null);

  /** API hooks */
  const {
    data: rootData,
    loading: rootLoading,
    error: rootError,
    fetchData: fetchRoot,
  } = useOneDrivePickerFiles<{
    items: Array<{
      id: string;
      name: string;
      size: number;
      file_format: string | null;
      creator: string;
      is_folder: boolean;
      mime_type: string;
    }>;
  }>();

  const {
    data: folderData,
    loading: folderLoading,
    error: folderError,
    fetchData: fetchFolder,
  } = useOneDriveFolderContents<{
    folder: { id: string; name: string };
    breadcrumbs: Array<{ id: string; name: string }>;
    items: Array<{
      id: string;
      name: string;
      size: number;
      file_format: string | null;
      creator: string;
      is_folder: boolean;
      mime_type: string;
    }>;
  }>(currentFolderId as OneDriveId);

  /** Reset state when the modal is opened */
  useEffect(() => {
    if (!isOpen) return;
    setSelectedItems([]);
    setCurrentFolderId(initialFolderId ?? null);
    latestFolderIdRef.current = initialFolderId ?? null;
    setBreadcrumbs([{ id: "root", name: "My files" }]);
  }, [isOpen, initialFolderId]);

  /** Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  /** Fetch root files when opened or when we return to root */
  useEffect(() => {
    if (!isOpen) return;
    if (!currentFolderId) {
      fetchRoot();
    }
  }, [isOpen, currentFolderId, fetchRoot]);

  /** Fetch folder contents on navigation */
  useEffect(() => {
    if (!isOpen) return;
    if (currentFolderId) {
      latestFolderIdRef.current = currentFolderId;
      fetchFolder();
    } else {
      setBreadcrumbs([{ id: "root", name: "My files" }]);
    }
  }, [isOpen, currentFolderId, fetchFolder]);

  /** Apply breadcrumbs only for the latest folder to avoid stale crumbs */
  useEffect(() => {
    if (!currentFolderId) return; // root handled elsewhere
    const latestId = latestFolderIdRef.current;
    const dataFolderId = folderData?.folder?.id ?? null;
    if (!folderData || !dataFolderId) return;
    if (latestId && dataFolderId === latestId) {
      setBreadcrumbs(normalizeBreadcrumbs(folderData.breadcrumbs || []));
    }
  }, [folderData, currentFolderId]);

  /** Map API -> FileItem[] */
  const mapApiItemsToFileItems = useCallback(
    (apiItems: any[] = []): FileItem[] => {
      return apiItems.map((i) => {
        const type: "file" | "folder" = i.is_folder ? "folder" : "file";
        const ext = i.is_folder
          ? undefined
          : getExtFromName(i.name) ?? undefined;
        return {
          id: i.id,
          name: i.name,
          type,
          size: type === "file" ? bytesToReadable(i.size) : undefined,
          modified: "-", // API sample doesn't include modified date
          modifiedBy: i.creator ?? "-",
          filetype: ext,
          mimeType: i.mime_type,
        };
      });
    },
    []
  );

  const currentFiles: FileItem[] = useMemo(() => {
    if (currentFolderId) {
      return mapApiItemsToFileItems(folderData?.items ?? []);
    }
    return mapApiItemsToFileItems(rootData?.items ?? []);
  }, [currentFolderId, rootData, folderData, mapApiItemsToFileItems]);

  /** Selection helpers (array-based) */
  const isSelected = useCallback(
    (id: string) => selectedItems.some((s) => s.id === id),
    [selectedItems]
  );

  const toggleSelect = (item: FileItem) => {
    setSelectedItems((prev) => {
      const exists = prev.some((s) => s.id === item.id);
      if (exists) {
        return prev.filter((s) => s.id !== item.id);
      }
      const upload: OneDriveFileUpload = {
        id: item.id,
        name: item.name,
        mimeType:
          item.mimeType ??
          (item.type === "folder" ? FOLDER_MIME : "application/octet-stream"),
      };
      return [...prev, upload];
    });
  };

  /** Navigation */
  const openFolder = (folder: FileItem) => {
    setCurrentFolderId(folder.id as OneDriveId);
  };

  const onBreadcrumbClick = (index: number) => {
    const target = breadcrumbs[index];
    if (!target) return;
    if (index === 0) {
      setCurrentFolderId(null);
      latestFolderIdRef.current = null;
      setBreadcrumbs([{ id: "root", name: "My files" }]);
      fetchRoot();
    } else {
      setCurrentFolderId(target.id as OneDriveId);
      latestFolderIdRef.current = target.id as OneDriveId;
    }
  };

  const handleConfirm = () => {
    onConfirm?.(selectedItems);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center w-full ml-[0px!important]"
      role="dialog"
      aria-modal="true"
      aria-label="OneDrive File Picker"
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white shadow-lg w-[900px] h-[600px] flex flex-col rounded-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with breadcrumbs */}
        <div className="p-4 border-b flex items-center space-x-1 text-sm bg-[#F9FAFB]">
          {breadcrumbs.map((crumb, i) => (
            <div
              key={`${crumb.id}-${i}`}
              className="flex items-center space-x-1"
            >
              <button
                type="button"
                onClick={() => onBreadcrumbClick(i)}
                className="text-base text-gray-700 hover:bg-gray-100 rounded-sm px-2 py-1 flex items-center space-x-1"
              >
                {crumb.name}
              </button>
              {i < breadcrumbs.length - 1 && (
                <FaChevronRight className="h-3 w-3 text-gray-400" />
              )}
            </div>
          ))}
        </div>

        {/* Column headers */}
        <div className="grid grid-cols-12 px-4 h-12 items-center border-b border-gray-100 text-sm font-medium bg-white">
          <div className="col-span-6">Name</div>
          <div className="col-span-2">Modified</div>
          <div className="col-span-4">Modified by</div>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-auto">
          {(rootLoading && !currentFolderId) ||
          (folderLoading && currentFolderId) ? (
            <div className="h-full flex items-center justify-center">
              <Loader size="lg" color="#2162EB" />
            </div>
          ) : (
            currentFiles.map((item) => {
              const selected = isSelected(item.id);
              return (
                <div
                  key={item.id}
                  className={`grid grid-cols-12 px-4 py-2 border-b border-gray-100 h-12 hover:bg-gray-50 ${
                    selected ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="col-span-6 flex items-center gap-3">
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.name}`}
                      checked={selected}
                      onChange={() => toggleSelect(item)}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        item.type === "folder" ? openFolder(item) : undefined
                      }
                      className={`flex items-center gap-2 ${
                        item.type === "folder"
                          ? "cursor-pointer"
                          : "cursor-default"
                      }`}
                    >
                      {item.type === "folder" ? (
                        <img
                          src={"/folder-icon.svg"}
                          alt="Folder"
                          className="w-6 h-6"
                        />
                      ) : (
                        <img
                          src={getFileIcon(item.filetype ?? "file")}
                          alt=""
                          className="h-6 w-6"
                        />
                      )}
                      <span className="text-sm font-medium truncate">
                        {item.name}
                      </span>
                    </button>
                    {item.size && (
                      <span className="text-xs text-gray-500">{item.size}</span>
                    )}
                  </div>
                  <div className="col-span-2 text-sm text-gray-500 flex items-center">
                    {item.modified}
                  </div>
                  <div className="col-span-4 text-sm text-gray-500 flex items-center truncate">
                    {item.modifiedBy}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-between items-center bg-[#F9FAFB]">
          {selectedItems.length > 0 && (
            <div className="text-sm flex items-center px-3 py-1 rounded-full bg-gray-50 border border-gray-200 space-x-1">
              <span>{`${selectedItems.length} selected`}</span>
              <button
                type="button"
                onClick={() => setSelectedItems([])}
                className="text-gray-500 hover:text-red-600 font-semibold"
                aria-label="Clear selection"
              >
                <IoCloseCircle className="h-5 w-5" />
              </button>
            </div>
          )}
          <div className="space-x-2 ml-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 border rounded hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={selectedItems.length === 0}
              className={`px-4 py-1.5 rounded text-white ${
                selectedItems.length === 0
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Confirmation: The provided code is complete and hasn’t been changed elsewhere.
 */
